import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';
import { API } from '../lib/api';

const DIVISIONS = [
  { value: 'open_m', label: 'Open — Men' },
  { value: 'open_f', label: 'Open — Women' },
  { value: 'pro_m', label: 'Pro — Men' },
  { value: 'pro_f', label: 'Pro — Women' },
  { value: 'masters_m', label: 'Masters 40+ — Men' },
  { value: 'masters_f', label: 'Masters 40+ — Women' },
];

const EXPERIENCE = [
  { value: 1, label: 'First HYROX' },
  { value: 2, label: '1–2 races completed' },
  { value: 3, label: '3–5 races' },
  { value: 4, label: '5+ races / competitive' },
];

const GOALS = [
  { value: 'finish', label: 'Just finish strong' },
  { value: 'sub75', label: 'Sub-75 minutes' },
  { value: 'sub60', label: 'Sub-60 minutes' },
  { value: 'sub50', label: 'Sub-50 minutes' },
  { value: 'podium', label: 'Podium / top 10%' },
];

const STATIONS = [
  { id: 'ski', label: 'SkiErg 1000m', unit: 'min', default: 4, min: 2, max: 10 },
  { id: 'sled_push', label: 'Sled Push 50m', unit: 'min', default: 2, min: 1, max: 8, step: 0.1 },
  { id: 'sled_pull', label: 'Sled Pull 50m', unit: 'min', default: 2.5, min: 1, max: 8, step: 0.1 },
  { id: 'burpee', label: 'Burpee Broad Jump 80m', unit: 'min', default: 4.5, min: 2, max: 12, step: 0.1 },
  { id: 'row', label: 'RowErg 1000m', unit: 'min', default: 3.8, min: 2, max: 10, step: 0.1 },
  { id: 'farmers', label: 'Farmers Carry 200m', unit: 'min', default: 2, min: 1, max: 7, step: 0.1 },
  { id: 'sandbag', label: 'Sandbag Lunges 100m', unit: 'min', default: 4, min: 2, max: 10, step: 0.1 },
  { id: 'wallball', label: 'Wall Balls x100', unit: 'min', default: 5, min: 2, max: 15, step: 0.1 },
];

function fmtTime(totalSec) {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = Math.round(totalSec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function fmtMins(totalSec) {
  const m = Math.floor(totalSec / 60);
  const s = Math.round(totalSec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getTier(totalSec) {
  if (totalSec < 50 * 60) return { tier: 'Elite Performance', cls: 'elite', badge: 'ELITE' };
  if (totalSec < 60 * 60) return { tier: 'Advanced Athlete', cls: 'elite', badge: 'SUB-60 CLUB' };
  if (totalSec < 75 * 60) return { tier: 'Strong Competitor', cls: 'advanced', badge: 'COMPETITIVE' };
  if (totalSec < 90 * 60) return { tier: 'Solid Performance', cls: 'intermediate', badge: 'INTERMEDIATE' };
  return { tier: 'Finisher — Keep Building', cls: 'beginner', badge: 'FINISHER' };
}

export default function HyroxFinishTimePage() {
  const { saveResult } = useResults();
  const resultsRef = useRef(null);

  // Form state
  const [division, setDivision] = useState('open_m');
  const [experience, setExperience] = useState(3);
  const [run5kMin, setRun5kMin] = useState(22);
  const [run5kSec, setRun5kSec] = useState(0);
  const [run10kMin, setRun10kMin] = useState(46);
  const [run10kSec, setRun10kSec] = useState(30);
  const [volume, setVolume] = useState(10);
  const [goal, setGoal] = useState('sub60');
  const [stations, setStations] = useState(
    Object.fromEntries(STATIONS.map(s => [s.id, s.default]))
  );

  // Results
  const [result, setResult] = useState(null);

  // Gate
  const [gateOpen, setGateOpen] = useState(false);
  const [gateName, setGateName] = useState('');
  const [gateEmail, setGateEmail] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [pendingResult, setPendingResult] = useState(null);
  const [ranking, setRanking] = useState(null);

  const updateStation = (id, val) => {
    setStations(prev => ({ ...prev, [id]: parseFloat(val) || 0 }));
  };

  const predict = () => {
    const run5k = run5kMin * 60 + run5kSec;
    const run10k = run10kMin * 60 + run10kSec;
    const pace10kPerKm = run10k / 10;
    const hyroxRunPace = pace10kPerKm * 1.12;
    const totalRunningTime = hyroxRunPace * 8;

    const stationSecs = {};
    STATIONS.forEach(s => { stationSecs[s.id] = (stations[s.id] || s.default) * 60; });
    const stationTotal = Object.values(stationSecs).reduce((a, b) => a + b, 0);

    // eslint-disable-next-line no-nested-ternary -- readable tier picker
    const transBase = experience === 4 ? 25 : experience === 3 ? 35 : experience === 2 ? 45 : 60;
    const transTotal = transBase * 8;
    // eslint-disable-next-line no-nested-ternary -- readable tier picker
    const volMult = volume >= 15 ? 0.95 : volume >= 10 ? 1.0 : volume >= 6 ? 1.05 : 1.10;
    const totalTime = Math.round(totalRunningTime + stationTotal * volMult + transTotal);

    const { tier, cls, badge } = getTier(totalTime);

    const splits = [
      { seg: 'Run 1km', type: 'RUN', time: hyroxRunPace },
      { seg: 'SkiErg 1000m', type: 'STATION', time: stationSecs.ski },
      { seg: 'Run 1km', type: 'RUN', time: hyroxRunPace },
      { seg: 'Sled Push 50m', type: 'STATION', time: stationSecs.sled_push },
      { seg: 'Run 1km', type: 'RUN', time: hyroxRunPace },
      { seg: 'Sled Pull 50m', type: 'STATION', time: stationSecs.sled_pull },
      { seg: 'Run 1km', type: 'RUN', time: hyroxRunPace },
      { seg: 'Burpee Broad Jump 80m', type: 'STATION', time: stationSecs.burpee },
      { seg: 'Run 1km', type: 'RUN', time: hyroxRunPace },
      { seg: 'RowErg 1000m', type: 'STATION', time: stationSecs.row },
      { seg: 'Run 1km', type: 'RUN', time: hyroxRunPace },
      { seg: 'Farmers Carry 200m', type: 'STATION', time: stationSecs.farmers },
      { seg: 'Run 1km', type: 'RUN', time: hyroxRunPace },
      { seg: 'Sandbag Lunges 100m', type: 'STATION', time: stationSecs.sandbag },
      { seg: 'Run 1km', type: 'RUN', time: hyroxRunPace },
      { seg: 'Wall Balls x100', type: 'STATION', time: stationSecs.wallball * volMult },
    ];

    const tips = [];
    const paceMin = Math.floor(pace10kPerKm / 60);
    const paceSec = Math.round(pace10kPerKm % 60);
    tips.push(`Target a HYROX running pace of ${paceMin}:${String(paceSec).padStart(2, '0')}/km — slightly slower than your 10K pace to account for station fatigue.`);
    if (stationSecs.wallball > 5.5 * 60) tips.push('Wall balls are your biggest time sink. Break into 3 sets of ~33 and minimize rest to under 10 seconds between sets.');
    if (stationSecs.burpee > 5 * 60) tips.push('Pace your burpee broad jumps — start at 75% effort. Going out too hot here kills your last 3km run split.');
    if (stationSecs.sled_push + stationSecs.sled_pull > 5 * 60) tips.push('Sled work: breathe through the push, recover on the pull. Consistency beats speed on both.');
    if (experience < 3) tips.push('Race day adrenaline will make your first 1km feel easy — hold back or you will blow up by the SkiErg.');
    if (volume < 8) tips.push('Your training volume suggests adding one more functional fitness session per week to improve station times.');
    tips.push(`Running is ${Math.round(totalRunningTime / totalTime * 100)}% of your race. ${totalRunningTime > stationTotal * volMult ? 'Focus on run training to make the biggest improvement.' : 'Station work is your main opportunity for time savings.'}`);

    const computed = {
      totalTime, tier, cls, badge, splits, tips,
      totalRunningTime, stationTotalAdj: stationTotal * volMult, transTotal,
    };

    if (unlocked) {
      showResults(computed);
    } else {
      setPendingResult(computed);
      setGateOpen(true);
    }
  };

  const showResults = (r) => {
    setResult(r);
    saveResult('hyroxFinishTime', {
      value: fmtTime(r.totalTime),
      details: `${r.tier} | Run: ${fmtMins(r.totalRunningTime)} | Stations: ${fmtMins(r.stationTotalAdj)}`,
    });
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const submitBenchmark = (r, email, name) => {
    // First-party data capture + lead + percentile ranking (best-effort).
    fetch(`${API}/hyrox/benchmark-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        first_name: name || '',
        division,
        experience,
        finish_sec: r.totalTime,
        running_sec: Math.round(r.totalRunningTime),
        station_sec: Math.round(r.stationTotalAdj),
        goal,
      }),
    })
      .then((res) => res.json())
      .then((d) => { if (d && d.percentile) setRanking(d); })
      .catch(() => {});
  };

  const unlockGate = () => {
    if (!gateEmail.includes('@')) return;
    setUnlocked(true);
    setGateOpen(false);
    if (pendingResult) {
      submitBenchmark(pendingResult, gateEmail.trim(), gateName.trim());
      showResults(pendingResult);
    }
  };

  const badgeColors = {
    elite: { bg: 'rgba(232,255,0,0.15)', border: '#e8ff00', text: '#e8ff00' },
    advanced: { bg: 'rgba(0,200,255,0.1)', border: '#00c8ff', text: '#00c8ff' },
    intermediate: { bg: 'rgba(255,120,0,0.1)', border: '#ff7800', text: '#ff7800' },
    beginner: { bg: 'rgba(255,255,255,0.06)', border: '#3f3f46', text: '#71717a' },
  };

  return (
    <ToolSEOWrapper toolKey="hyrox-finish-time">
    <main className="pt-20 min-h-screen relative" data-testid="hyrox-predictor-page">
      {/* Grid background */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        backgroundImage: 'linear-gradient(rgba(232,255,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(232,255,0,0.03) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      {/* Gate Overlay */}
      {gateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }} data-testid="gate-overlay">
          <div className="relative bg-[#161616] border border-[#e8ff00] max-w-md w-full p-10 text-center" style={{ animation: 'fadeUp 0.3s ease' }}>
            <button onClick={() => setGateOpen(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="gate-close"><X className="w-5 h-5" /></button>
            <p className="font-mono text-[10px] tracking-[0.2em] text-[#e8ff00] uppercase mb-4">NinjAthlete Intel</p>
            <h3 className="font-heading text-4xl text-white mb-3">Unlock Your Full Prediction</h3>
            <p className="text-sm text-zinc-500 leading-relaxed mb-8">Get your detailed station splits, performance tier, and race strategy — plus the NinjAthlete newsletter.</p>
            <input
              className="w-full bg-[#0a0a0a] border border-zinc-800 text-white px-4 py-3.5 mb-3 focus:border-[#e8ff00] focus:outline-none"
              placeholder="First name" value={gateName} onChange={e => setGateName(e.target.value)}
              data-testid="gate-name-input"
            />
            <input
              className="w-full bg-[#0a0a0a] border border-zinc-800 text-white px-4 py-3.5 mb-3 focus:border-[#e8ff00] focus:outline-none"
              type="email" placeholder="Email address" value={gateEmail} onChange={e => setGateEmail(e.target.value)}
              data-testid="gate-email-input"
            />
            <button onClick={unlockGate} className="w-full bg-[#e8ff00] text-black font-heading text-xl tracking-wider py-4 hover:bg-white transition-colors" data-testid="gate-unlock-btn">
              UNLOCK MY PREDICTION
            </button>
            <p className="font-mono text-[9px] text-zinc-600 mt-4 tracking-wider leading-relaxed">No spam. Unsubscribe anytime. Science over hype.</p>
          </div>
        </div>
      )}

      <div className="relative z-10 max-w-[720px] mx-auto px-6 pb-20">
        {/* Back */}
        <Link to="/tools" className="inline-flex items-center gap-2 text-zinc-500 hover:text-[#e8ff00] text-sm mb-8 transition-colors" data-testid="back-to-tools">
          <ArrowLeft className="w-4 h-4" /> Tools
        </Link>

        {/* Header */}
        <div className="mb-12">
          <p className="font-mono text-[10px] tracking-[0.25em] text-[#e8ff00] uppercase mb-4 flex items-center gap-2">
            <span className="inline-block w-5 h-px bg-[#e8ff00]" /> NinjAthlete HYROX Tools
          </p>
          <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl leading-[0.9] text-white mb-4" data-testid="predictor-title">
            HYROX<span className="block text-[#e8ff00]">FINISH TIME<br/>PREDICTOR</span>
          </h1>
          <p className="text-sm text-zinc-500 leading-relaxed max-w-md border-l-2 border-zinc-800 pl-4">
            Enter your current fitness benchmarks and we'll predict your HYROX finish time — broken down by running splits and station times.
          </p>
        </div>

        {/* Step 1 */}
        <StepLabel num={1} label="Athlete Profile" />
        <Card title="Your Stats">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Division">
              <Select value={division} onChange={e => setDivision(e.target.value)} options={DIVISIONS} testId="division-select" />
            </Field>
            <Field label="Experience Level">
              <Select value={experience} onChange={e => setExperience(parseInt(e.target.value))} options={EXPERIENCE} testId="experience-select" />
            </Field>
          </div>
        </Card>

        {/* Step 2 */}
        <StepLabel num={2} label="Running Benchmarks" />
        <Card title="Running Performance">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="5K Minutes">
              <NumInput value={run5kMin} onChange={setRun5kMin} min={12} max={60} testId="5k-min" />
            </Field>
            <Field label="5K Seconds">
              <NumInput value={run5kSec} onChange={setRun5kSec} min={0} max={59} testId="5k-sec" />
            </Field>
            <Field label="10K Minutes">
              <NumInput value={run10kMin} onChange={setRun10kMin} min={25} max={120} testId="10k-min" />
            </Field>
            <Field label="10K Seconds">
              <NumInput value={run10kSec} onChange={setRun10kSec} min={0} max={59} testId="10k-sec" />
            </Field>
          </div>
        </Card>

        {/* Step 3 */}
        <StepLabel num={3} label="Station Benchmarks" />
        <Card title="Functional Fitness Benchmarks">
          <p className="font-mono text-[11px] text-zinc-600 tracking-wider mb-5">Enter your best single-set times. Leave as default if unsure.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STATIONS.map(s => (
              <div key={s.id} className="flex flex-col gap-1.5">
                <label className="font-mono text-[9px] tracking-[0.15em] text-zinc-500 uppercase">{s.label}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" value={stations[s.id]} step={s.step || 1} min={s.min} max={s.max}
                    onChange={e => updateStation(s.id, e.target.value)}
                    className="flex-1 bg-[#0a0a0a] border border-zinc-800 text-white font-mono text-sm px-3 py-2.5 focus:border-[#e8ff00] focus:outline-none"
                    data-testid={`station-${s.id}`}
                  />
                  <span className="font-mono text-[10px] text-zinc-600">{s.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Step 4 */}
        <StepLabel num={4} label="Race Day Factors" />
        <Card title="Conditioning & Readiness">
          <Field label={`Weekly training volume — ${volume}h/wk`}>
            <input
              type="range" min={3} max={20} value={volume} onChange={e => setVolume(parseInt(e.target.value))}
              className="w-full h-0.5 bg-zinc-800 appearance-none cursor-pointer accent-[#e8ff00]"
              data-testid="volume-slider"
              style={{ accentColor: '#e8ff00' }}
            />
            <div className="flex justify-between mt-1">
              <span className="font-heading text-3xl text-[#e8ff00]">{volume}h/wk</span>
              <span className="font-mono text-[9px] text-zinc-600 self-end">3h — 20h</span>
            </div>
          </Field>
          <Field label="Race goal" className="mt-4">
            <Select value={goal} onChange={e => setGoal(e.target.value)} options={GOALS} testId="goal-select" />
          </Field>
        </Card>

        {/* CTA */}
        <button
          onClick={predict}
          className="w-full bg-[#e8ff00] text-black font-heading text-xl tracking-[0.1em] py-5 flex items-center justify-center gap-3 hover:bg-white transition-colors mt-2"
          data-testid="predict-btn"
        >
          PREDICT MY FINISH TIME <ArrowRight className="w-5 h-5" />
        </button>

        {/* Results */}
        {result && (
          <div ref={resultsRef} className="mt-8" style={{ animation: 'fadeUp 0.4s ease' }} data-testid="prediction-results">
            <StepLabel num="✓" label="Your Prediction" />

            {/* Hero */}
            <div className="relative bg-[#111] border border-[#e8ff00] p-10 text-center overflow-hidden mb-5" data-testid="result-hero">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="font-heading text-[140px] text-[#e8ff00] opacity-[0.03] tracking-wider">FINISH</span>
              </div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-3">Predicted Finish Time</p>
              <p className="font-heading text-7xl sm:text-8xl text-[#e8ff00] leading-none mb-2" data-testid="result-time">
                {fmtTime(result.totalTime)}
              </p>
              <p className="font-mono text-xs tracking-[0.15em] text-white uppercase" data-testid="result-tier">{result.tier}</p>
              <span
                className="inline-block mt-3 px-3 py-1 font-mono text-[10px] tracking-[0.15em] uppercase"
                style={{
                  background: badgeColors[result.cls].bg,
                  border: `1px solid ${badgeColors[result.cls].border}`,
                  color: badgeColors[result.cls].text,
                }}
                data-testid="result-badge"
              >
                {result.badge}
              </span>
            </div>

            {/* Percentile ranking vs benchmark dataset */}
            {ranking && ranking.percentile && (
              <div className="bg-[#161616] border border-[#e8ff00]/40 p-5 mb-5 text-center" data-testid="ranking-card">
                <p className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase mb-1">Your Ranking · {ranking.division_label}</p>
                <p className="text-white text-lg">
                  You're faster than <span className="font-heading text-3xl text-[#e8ff00] align-middle">{ranking.percentile}%</span> of athletes
                </p>
                <Link to="/hyrox/benchmark-report" className="inline-flex items-center gap-1.5 text-[#00c8ff] text-xs mt-2 hover:text-white transition-colors" data-testid="ranking-report-link">
                  See the full HYROX Benchmark Report <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            )}

            {/* Breakdown */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { val: fmtMins(result.totalRunningTime), lbl: 'Running Time' },
                { val: fmtMins(result.stationTotalAdj), lbl: 'Station Time' },
                { val: fmtMins(result.transTotal), lbl: 'Transitions' },
              ].map(b => (
                <div key={b.lbl} className="bg-[#161616] border border-zinc-800 p-4 text-center">
                  <span className="block font-heading text-2xl text-white" data-testid={`bd-${b.lbl.toLowerCase().replace(' ', '-')}`}>{b.val}</span>
                  <span className="block font-mono text-[9px] tracking-wider text-zinc-500 uppercase mt-1">{b.lbl}</span>
                </div>
              ))}
            </div>

            {/* Save */}
            <div className="mb-5">
              <SaveResultButton
                onSave={() => saveResult('hyroxFinishTime', {
                  value: fmtTime(result.totalTime),
                  details: `${result.tier} | Run: ${fmtMins(result.totalRunningTime)} | Stations: ${fmtMins(result.stationTotalAdj)}`,
                })}
              />
            </div>

            {/* Splits Table */}
            <Card title="Station-by-Station Splits">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="splits-table">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      {['#', 'Segment', 'Type', 'Est. Time'].map(h => (
                        <th key={h} className="font-mono text-[9px] tracking-[0.15em] text-zinc-500 uppercase text-left px-3 py-2">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.splits.map((s, i) => (
                      <tr key={s.time} className="border-b border-zinc-800/50 hover:bg-[#e8ff0005]">
                        <td className="font-mono text-xs text-zinc-600 px-3 py-3">{i + 1}</td>
                        <td className={`font-mono text-xs px-3 py-3 ${s.type === 'STATION' ? 'text-[#e8ff00]' : 'text-white'}`}>{s.seg}</td>
                        <td className="font-mono text-[10px] text-zinc-600 px-3 py-3">{s.type}</td>
                        <td className="font-mono text-xs text-[#e8ff00] px-3 py-3">{fmtMins(s.time)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Tips */}
            <Card title="Race Strategy Tips">
              <div className="flex flex-col gap-3" data-testid="tips-list">
                {result.tips.map((t, i) => (
                  <div key={t} className="flex gap-3 items-start text-sm text-zinc-400 leading-relaxed">
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-[#e8ff0015] border border-[#e8ff0030] text-[#e8ff00] text-[10px] mt-0.5">
                      <ArrowRight className="w-3 h-3" />
                    </span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </Card>

            <p className="font-mono text-[10px] text-zinc-600 tracking-wider leading-relaxed border-t border-zinc-800 pt-6 mt-8">
              Predictions based on HYROX race data, your fitness benchmarks, and performance modeling.<br />
              Individual results vary. Built by NinjAthlete — science over hype.<br />
              <span className="text-[#e8ff00]">ninjathlete.com</span>
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 16px; height: 16px;
          background: #e8ff00;
          cursor: pointer;
          transform: rotate(45deg);
        }
        input[type="range"]::-moz-range-thumb {
          width: 16px; height: 16px;
          background: #e8ff00;
          border: none;
          cursor: pointer;
        }
      `}</style>
    </main>
    </ToolSEOWrapper>
  );
}

/* Reusable sub-components */
function StepLabel({ num, label }) {
  return (
    <div className="flex items-center gap-2.5 mb-5 mt-8">
      <span className="w-[22px] h-[22px] rounded-full border border-[#e8ff00] flex items-center justify-center font-mono text-[9px] text-[#e8ff00]">{num}</span>
      <span className="font-mono text-[10px] tracking-[0.2em] text-[#e8ff00] uppercase">{label}</span>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="relative bg-[#161616] border border-zinc-800 p-6 sm:p-8 mb-5 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, #e8ff00, transparent)' }} />
      {title && <h3 className="font-heading text-xl tracking-wider text-white mb-5">{title}</h3>}
      {children}
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">{label}</label>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, min, max, testId }) {
  return (
    <input
      type="number" value={value} min={min} max={max}
      onChange={e => onChange(parseFloat(e.target.value) || 0)}
      className="bg-[#0a0a0a] border border-zinc-800 text-white font-mono text-base px-3.5 py-3 w-full focus:border-[#e8ff00] focus:outline-none"
      data-testid={testId}
    />
  );
}

function Select({ value, onChange, options, testId }) {
  return (
    <select
      value={value} onChange={onChange}
      className="bg-[#0a0a0a] border border-zinc-800 text-white font-mono text-sm px-3.5 py-3 w-full focus:border-[#e8ff00] focus:outline-none appearance-none cursor-pointer"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 14px center',
        paddingRight: '36px',
      }}
      data-testid={testId}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
